import { computed, makeObservable, observable } from 'mobx'
import { type IIconsVector } from 'shared/ui/Icon/icons'
import { type IInboxKey } from 'entities/Inbox/model/types'
import { NAME_INBOX, TYPES_INBOX } from 'entities/Inbox/store/types'
import { type IResponseInboxGroup } from 'entities/Inbox/api/types'
import { type ICallSettingsItem } from 'entities/Organization/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { BaseInbox } from 'entities/Inbox/model/BaseInbox'

export class GroupInbox extends BaseInbox<IResponseInboxGroup> {
  id: number
  type: 'inbox_group'
  teamsIds!: number[]
  is_rename!: boolean
  description!: string
  isMuted = false
  root_tree!: string
  color_coding_enabled!: boolean
  origin!: IResponseInboxGroup
  isPFTInbox = false
  call_via_aircall!: ICallSettingsItem | null
  created_at!: string

  constructor(item: IResponseInboxGroup, private _entities: IEntitiesStore) {
    super(item)

    this.id = item.id
    this.type = 'inbox_group'
    this.syncOrigin(item)

    makeObservable(this, {
      name: observable,
      is_rename: observable,
      is_favorite: observable,
      root_tree: observable,
      teamsIds: observable,
      unread: computed,
      unread_calls: computed,
      unread_messages_count: computed,
      unread_mentions_count: computed,
      unreadCount: computed,
    })
  }

  removeTeamId(id: number) {
    this.teamsIds = [...this.teamsIds.filter((item) => item !== id)]
  }

  handleUpdateTeamsIds = (teamsIds: number[]) => {
    this.teamsIds = teamsIds
  }

  syncOrigin = (origin: IResponseInboxGroup & { root_tree?: string; is_rename?: boolean }) => {
    this.id = origin.id
    this.is_favorite = origin.is_favorite
    this.name = origin.name
    this.teamsIds = origin.teams.map((team) => team.id)
    this.is_rename = origin.is_rename || false
    this.description = origin.description || ''
    this.root_tree = origin.root_tree || this.initRootTree()
    this.color_coding_enabled = origin.color_coding_enabled
    this.origin = origin
    this.call_via_aircall = origin.call_via_aircall
    this.created_at = origin.created_at
  }

  get isCreateGroup() {
    return this.id === NAME_INBOX.Create
  }

  get icon(): IIconsVector {
    return 'folder'
  }

  get inboxType() {
    return TYPES_INBOX.GROUP
  }

  get typeKey(): IInboxKey {
    return 'group'
  }

  get bodyUpdateUserTeam() {
    return {
      id: this.id,
      type: this.inboxType,
    }
  }

  get bodyUpdateTeamsGroupsById() {
    return {
      name: this.name,
      description: this.description,
      team_ids: this.teamsIds,
    }
  }

  get idTree() {
    return `${this.root_tree}_${this.id}`
  }

  get isCallViaAircall() {
    return this.call_via_aircall?.is_active
  }

  get unread() {
    return this.teamsIds.reduce((state, id) => {
      const inbox = this._entities.inboxesStore.getItemShared(id)

      return inbox ? state + inbox.unread : state
    }, 0)
  }

  get unread_calls() {
    return this.teamsIds.reduce((state, id) => {
      const inbox = this._entities.inboxesStore.getItemShared(id)

      return inbox ? state + inbox.unread_calls : state
    }, 0)
  }

  get unread_messages_count() {
    return this.teamsIds.reduce((state, id) => {
      const inbox = this._entities.inboxesStore.getItemShared(id)

      return inbox ? state + inbox.unread_messages_count : state
    }, 0)
  }

  get unread_mentions_count() {
    return this.teamsIds.reduce((state, id) => {
      const inbox = this._entities.inboxesStore.getItemShared(id)

      return inbox ? state + inbox.unread_mentions_count : state
    }, 0)
  }

  get unreadCount() {
    return this.unread_messages_count + this.unread_mentions_count
  }
}
